import React from 'react'
import PropTypes from 'prop-types'
import { ErrorBoundary } from 'react-error-boundary'
import * as _ from 'lodash'

import { AppLayoutHeader } from './AppLayoutHeader'
import { AppLayoutMenubar } from './AppLayoutMenubar'
import { AppLayoutFooter } from './AppLayoutFooter'
import { useOnClickOutside } from '../../lib/OnClickOutside'
import { useGetQuery } from '../../lib/api/get'
import { useGetMutateCall } from '../../lib/api/get'
import { useUpdateCall } from '../../lib/api/update'
import { useAuthStore } from '../../store/auth'

import { Profile } from '../profile/Profile'
import { Messages } from '../messages/Messages'
import { Settings } from '../settings/Settings'
import { AppLayoutModuleNotFound } from './AppLayoutModuleNotFound'
import { AppLayoutError } from './AppLayoutError'
import { BasicForm } from '../basicForm/BasicForm'
import { Dashboard } from '../dashboard/Dashboard'
import { Projects } from '../projects/Projects'
import { CheckInOut } from '../checkInOut'
import { ChildAttendance } from '../childAttendance'
import { ChildAttendanceReport } from '../reports/ChildAttendanceReport'
import { KidsAttendance } from '../reports/KidsAttendance'
import { ManageAttendance } from '../childAttendance/ManageAttendance'
import { ManageStaffAttendance } from '../staffAttendance/ManageStaffAttendance'
import { StaffAttendance } from '../staffAttendance'
import { StaffPasscode } from '../staffPasscode'
import { StaffAttendanceReport } from '../reports/StaffAttendanceReport'
import { Organizations } from '../organizations'
import { ClassAttendance } from '../classAttendance'
//import { AdsComponent } from '../../common/AdComponent'

import { ChatHome } from '../chat'
import { StudentAttendanceReport } from '../reports/StudentAttendanceReport'
import { Expenses } from '../expenses'
import { ExpenseReport } from '../reports/ExpenseReport'
import { MarksEntry } from '../exams/MarksEntry'
import { ClassPromotions } from '../classPromotions'

/**
 * AppLayout Component
 *
 * The main layout component that wraps the entire application, including header, sidebar, and footer.
 * Dynamically loads and renders components based on the route configuration.
 *
 * @module appLayout/AppLayout
 * @description The main layout component that wraps the entire application, including header, sidebar, and footer.
 * Dynamically loads and renders components based on the route configuration.
 * @param {Object} envData - Environment data including API URLs and configuration.
 * @param {Object} authDetails - User authentication details.
 * @param {Object} routeData - Route data, including API endpoint schema and component name.
 * @param {string} isLocalEnvironment - The current environment (e.g., local, production).
 * @param {Object} appDataParsed - Parsed application data including routes.
 * @example
 * <AppLayout
 *   envData={envData}
 *   authDetails={authDetails}
 *   routeData={routeData}
 *   isLocalEnvironment={isLocalEnvironment}
 *   appDataParsed={appDataParsed}
 * />
 *
 * @returns {React.ReactElement} The rendered AppLayout component.
 */
export const AppLayout = ({
  envData,
  authDetails,
  routeData,
  isLocalEnvironment,
  appDataParsed,
  gettingDeps,
  allData,
  setActiveOrgRoutes,
  activeOrgRoutes,
  isDefaultOrg,
  setIsDefaultOrg,
}) => {
  const [toggleMenu, setToggleMenu] = React.useState(false)
  const [activeOrg, setActiveOrg] = React.useState(
    sessionStorage.getItem('defApp') ? JSON.parse(sessionStorage.getItem('defApp')).orgName : null,
  )
  // const [isDefaultOrg, setIsDefaultOrg] = React.useState(
  //   (authDetails.dependeciesFormAPIS?.userSettings?.formData?.userSettings &&
  //     JSON.parse(authDetails.dependeciesFormAPIS?.userSettings?.formData?.userSettings)) ||
  //     null,
  // )
  const [defaultApp, setDefaultApp] = React.useState(
    sessionStorage.getItem('defApp') ? JSON.parse(sessionStorage.getItem('defApp')) : null,
  )
  const getCallMutation = useGetMutateCall()
  const updateCallMutation = useUpdateCall()

  const { unSetOrgRole, setOrgRole, setAuthentication } = useAuthStore()

  const unsetOrg = () => {
    sessionStorage.removeItem('defApp')
    sessionStorage.removeItem('mainMenu')
    sessionStorage.removeItem('activeRoutes')
    setActiveOrg(null)
    setActiveOrgRoutes(null)
    unSetOrgRole()
    window.location.href = '/console/dashboard'
  }
  const sideMenuRef = React.useRef()
  useOnClickOutside(sideMenuRef, () => setToggleMenu(false))

  const moduleSchema =
    routeData.apiEndPointSchema !== '' || routeData.apiEndPointSchema !== null
      ? JSON.parse(routeData.apiEndPointSchema)
      : null

  const paramToRole = () => {
    let paramTo = ''

    moduleSchema.addParamToRoleArray &&
      moduleSchema.addParamToRoleArray.length > 0 &&
      moduleSchema.addParamToRoleArray.map((e) => {
        if (authDetails.role === e) {
          paramTo = moduleSchema.addParamToRoleString
        }
      })
    return paramTo
  }

  const getData = useGetQuery({
    apiURL: isLocalEnvironment + '/getData',
    apiEndpoint: moduleSchema.api || '',
    params: moduleSchema.moduleAddParam
      ? moduleSchema.moduleAddParamString + `${moduleSchema.addParamToRole ? paramToRole() : ''}`
      : `${moduleSchema.addParamToRole ? paramToRole() : ''}` +
        `&roleId=${authDetails.roleId}&pageName=${moduleSchema.moduleName}${moduleSchema.addParamToRole ? paramToRole() : ''}`,
    enabled: !!moduleSchema.api,
    isDynamic: true,
  })

  const Modules = {
    Profile: Profile,
    Messages: Messages,
    Settings: Settings,
    BasicForm: BasicForm,
    Dashboard: Dashboard,
    Projects: Projects,
    CheckInOut: CheckInOut,
    ChildAttendance: ChildAttendance,
    ChildAttendanceReport: ChildAttendanceReport,
    KidsAttendance: KidsAttendance,
    ManageAttendance: ManageAttendance,
    ManageStaffAttendance: ManageStaffAttendance,
    StaffAttendance: StaffAttendance,
    StaffPasscode: StaffPasscode,
    StaffAttendanceReport: StaffAttendanceReport,
    StudentAttendanceReport: StudentAttendanceReport,
    ExpenseReport: ExpenseReport,
    Chat: ChatHome,
    Organizations: Organizations,
    ClassAttendance: ClassAttendance,
    ClassPromotions: ClassPromotions,
    Expenses: Expenses,
    MarksEntry: MarksEntry,
  }

  const ModuleName = Modules[routeData.component]
  const modSchema = JSON.parse(routeData.apiEndPointSchema)

  const setApp = (app, orgType) => {
    sessionStorage.removeItem('defApp')
    sessionStorage.removeItem('activeRoutes')
    setDefaultApp(app)
    sessionStorage.setItem('defApp', JSON.stringify(app))
    sessionStorage.setItem('defAppType', orgType)
    setActiveOrg(app.orgName)
    getCallMutation
      .mutateAsync({
        apiURL: isLocalEnvironment + '/getData',
        apiEndpoint: 'orgAppData',
        params: `&targetTenantId=${app.tenantId}&targetAppId=${app.appId}&targetOrgId=${app.orgId}`,
      })
      .then((resp) => {
        sessionStorage.setItem(
          'activeRoutes',
          JSON.stringify({
            formData: { Pages: resp.formData.pageData, orgRoles: resp.formData.userRoles },
          }),
        )
        setActiveOrgRoutes({
          formData: { Pages: resp.formData.pageData, orgRoles: resp.formData.userRoles },
        })

        const orgRoleDetails = resp.formData?.userRoles?.filter((f) => f.isDefault === 1)
        setOrgRole(orgRoleDetails[0].rowFormData.roleName, orgRoleDetails[0].rowFormData.roleId)
        setAuthentication({
          ...authDetails,
          resObject: authDetails.authentication,
          orgRoleId: orgRoleDetails[0].rowFormData.roleId,
          orgRole: orgRoleDetails[0].rowFormData.roleName,
        })
      })
  }

  const removeDefaultApp = async (org) => {
    const usrSettings = JSON.parse(sessionStorage.getItem('auth')).state.session.user.userSetting
    let formData = usrSettings
    let data
    const orgs = isDefaultOrg?.favouriteOrgs ? isDefaultOrg.favouriteOrgs : []
    const removedFO = orgs.filter((f) => f.orgId !== org.orgId)
    data = { ...isDefaultOrg, favouriteOrgs: removedFO }
    formData['userSettings'] = JSON.stringify(data)
    delete formData['orgId']

    await updateCallMutation
      .mutateAsync({
        url: isLocalEnvironment + '/updateData',
        data: formData, //data,
        id: formData.userSettingsId,
        apiEndpoint: 'userSettings',
        params: '',
        isDynamic: true,
        messageTitle: 'Preferences',
        message: 'Preferences updated successfully!',
      })
      .then(() => {
        setIsDefaultOrg(JSON.parse(formData.userSettings))
      })
  }

  const setDefaultsApp = async (org, favourite = false) => {
    const usrSettings = JSON.parse(sessionStorage.getItem('auth')).state.session.user.userSetting
    let formData = usrSettings
    let data
    if (favourite) {
      const orgs = isDefaultOrg?.favouriteOrgs ? isDefaultOrg.favouriteOrgs : []
      const favouriteOrgs = [
        ...orgs,
        {
          orgId: org.orgId,
          orgType: org.orgType,
          orgName: org.orgName,
          tenantId: org.tenantId,
          appId: org.appId,
        },
      ]
      const uniquedFO = _.uniqWith(favouriteOrgs, _.isEqual) //_.uniqBy(favouriteOrgs, 'orgId')
      data = { ...isDefaultOrg, favouriteOrgs: uniquedFO }
      formData['userSettings'] = JSON.stringify(data)
    } else {
      const orgs = isDefaultOrg?.favouriteOrgs ? isDefaultOrg.favouriteOrgs : []
      const favouriteOrgs = [
        ...orgs,
        { orgId: org.orgId, orgName: org.orgName, tenantId: org.tenantId, appId: org.appId },
      ]
      const uniquedFO = _.uniqWith(favouriteOrgs, _.isEqual)
      data = { ...isDefaultOrg, orgId: org.orgId, orgDetails: org, favouriteOrgs: uniquedFO }
      formData['userSettings'] = JSON.stringify(data)
    }
    delete formData['orgId']
    await updateCallMutation
      .mutateAsync({
        url: isLocalEnvironment + '/updateData',
        data: formData, //data,
        id: formData.userSettingsId,
        apiEndpoint: 'userSettings',
        params: '',
        isDynamic: true,
        messageTitle: 'Preferences',
        message: 'Preferences updated successfully!',
      })
      .then(() => {
        setIsDefaultOrg(JSON.parse(formData.userSettings))
        if (!favourite) {
          setApp(org)
        }
      })
  }

  return (
    <>
      <AppLayoutHeader
        envData={envData}
        authDetails={authDetails}
        toggleMenu={toggleMenu}
        setToggleMenu={setToggleMenu}
        sideMenuRef={sideMenuRef}
        modSchema={modSchema}
        activeOrg={activeOrg}
        unsetOrg={unsetOrg}
        activeOrgRoutes={activeOrgRoutes}
        setIsDefaultOrg={setIsDefaultOrg}
        isDefaultOrg={isDefaultOrg}
        removeDefaultApp={removeDefaultApp}
        setApp={setApp}
      />
      {!modSchema.noLayout && (
        <>
          <AppLayoutMenubar
            key={activeOrg}
            envData={envData}
            toggleMenu={toggleMenu}
            setToggleMenu={setToggleMenu}
            sideMenuRef={sideMenuRef}
            authDetails={authDetails}
            routesData={appDataParsed.routesData}
            activeOrgRoutes={activeOrgRoutes}
            activeOrg={activeOrg}
            unsetOrg={unsetOrg}
          />
        </>
      )}
      <div className={!modSchema.noLayout ? 'trtui-main-content' : ''}>
        <div
          className={!modSchema.noLayout ? 'trtui-page-content' : ''}
          style={{ minHeight: '100vh' }}
        >
          <div className="trtui-container-fluid" key={getData}>
            {getData.isLoading || gettingDeps ? (
              <div>
                <div
                  id="status"
                  className="trtui-align-items-center trtui-d-none trtui-d-md-block trtui-justify-content-center"
                  style={{
                    width: '40px',
                    height: '40px',
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    margin: '-20px 0 0 6.4rem',
                  }}
                >
                  <div
                    className="trtui-spinner-border trtui-text-primary trtui-avatar-sm"
                    role="status"
                  >
                    <span className="trtui-visually-hidden">Loading...</span>
                  </div>
                </div>
                <div
                  id="status"
                  className="trtui-align-items-center trtui-d-block trtui-d-md-none trtui-justify-content-center"
                  style={{
                    width: '40px',
                    height: '40px',
                    position: 'absolute',
                    left: '20%',
                    top: '50%',
                    margin: '-20px 0 0 6.4rem',
                  }}
                >
                  <div
                    className="trtui-spinner-border trtui-text-primary trtui-avatar-sm"
                    role="status"
                  >
                    <span className="trtui-visually-hidden">Loading...</span>
                  </div>
                </div>
              </div>
            ) : ModuleName ? (
              <ErrorBoundary FallbackComponent={AppLayoutError}>
                {/* <AdsComponent dataAdSlot="7827988700" /> */}
                {routeData === 'dashboard' ? (
                  <Dashboard />
                ) : (
                  <ModuleName
                    authDetails={authDetails}
                    routeData={routeData}
                    isLocalEnvironment={isLocalEnvironment}
                    envData={envData}
                    defaultData={getData.data}
                    allData={allData}
                    setActiveOrg={setActiveOrg}
                    setActiveOrgRoutes={setActiveOrgRoutes}
                    activeOrgRoutes={activeOrgRoutes}
                    setIsDefaultOrg={setIsDefaultOrg}
                    isDefaultOrg={isDefaultOrg}
                    defaultApp={defaultApp}
                    setApp={setApp}
                    setDefaultApp={setDefaultsApp}
                  />
                )}
              </ErrorBoundary>
            ) : (
              <AppLayoutModuleNotFound />
            )}
          </div>
        </div>
      </div>
      <AppLayoutFooter modSchema={modSchema} />
    </>
  )
}

AppLayout.propTypes = {
  envData: PropTypes.object.isRequired,
  authDetails: PropTypes.object.isRequired,
  routeData: PropTypes.object.isRequired,
  isLocalEnvironment: PropTypes.string.isRequired,
  appDataParsed: PropTypes.object.isRequired,
  gettingDeps: PropTypes.bool,
  allData: PropTypes.object,
  setActiveOrgRoutes: PropTypes.func,
  activeOrgRoutes: PropTypes.object,
  isDefaultOrg: PropTypes.object,
  setIsDefaultOrg: PropTypes.func,
}
